import { useContext, useEffect, useState } from 'react';
import Loading from '../../../../components/Loading';
import { EmptyDataRow, Table, TableRowContainer } from '../../../../components/Table';
import emissionSpoolsContext from '../../../../context/emissionSpools/emissionSpoolsContext';
import { EmissionSpoolDto } from '../../../../context/emissionSpools/emissionSpoolsType';
import useEmissionSpools from '../../../../hooks/web3/vault/useEmissionSpools';
import useLatestProposal from '../../../../hooks/web3/vault/useLatestProposal';
import EmissionSpool from './EmissionSpoolsRow';
import useUserVoteForProposal from '../../../../hooks/web3/vault/useUserVoteForProposal';
import EmmisionSpoolHeaders from './EmissionSpoolsHeader';
import EmissionSpoolsVote from './EmissionSpoolsVote';
import { TabsContext } from '../../../../components/Tabs';

const EmissionSpools: React.FC = () => {
    const {
        activeSort,
        searchTerm,
        userHasVoted,
        filterData,
        sort,
        setTotalInputAlloc,
        setTotalAllocAmount,
        setUserHasVoted,
    } = useContext(emissionSpoolsContext);
    const { updateHeight } = useContext(TabsContext);

    const { emissionSpools, isLoading } = useEmissionSpools();
    const { latestProposal } = useLatestProposal();
    const { userVoteForProposal } = useUserVoteForProposal();

    const [selectedAccordion, setSelectedAccordion] = useState<string | null>(null);
    const [filteredData, setFilteredData] = useState<EmissionSpoolDto[]>([]);

    const toggle = (spoolAddress: string) => {
        setSelectedAccordion((prev) => (prev === spoolAddress ? null : spoolAddress));
    };

    useEffect(() => {
        updateHeight();
    }, [updateHeight, emissionSpools]);

    useEffect(() => {
        if (userVoteForProposal?.choice) {
            setUserHasVoted(true);
            return;
        }
        setUserHasVoted(false);
    }, [userVoteForProposal, setUserHasVoted]);

    useEffect(() => {
        if (!emissionSpools) return;

        if (userHasVoted) {
            emissionSpools.forEach(({ address }, index) => {
                const value = userVoteForProposal?.choice?.[+index + 1]
                    ? userVoteForProposal?.choice[+index + 1]
                    : '';

                setTotalInputAlloc({ [address]: value });
            });
        }

        const filtered = emissionSpools.filter((data) => filterData(data, searchTerm));

        setFilteredData(filtered);

        setTotalAllocAmount(0);

        filtered.forEach((spoolDetails) => {
            if (!userHasVoted) {
                setTotalInputAlloc({ [spoolDetails?.address]: '' });
            }
        });

        setTotalAllocAmount(
            emissionSpools.reduce(
                (accAllocAmount, emissionSpool) =>
                    accAllocAmount + emissionSpool.allocAmount,
                0
            )
        );
    }, [
        userHasVoted,
        searchTerm,
        emissionSpools,
        userVoteForProposal,
        filterData,
        setTotalInputAlloc,
        setTotalAllocAmount,
    ]);

    return (
        <Table
            numOfColumns={5}
            headers={<EmmisionSpoolHeaders hasFilteredResults={!!filteredData.length} />}
            dataLength={emissionSpools?.length || 0}
            className='emission-spools-table'
        >
            {!latestProposal ? (
                <EmptyDataRow text='No proposals exists.' />
            ) : (
                <Loading isLoading={isLoading} text='Loading Emission Spools...'>
                    <TableRowContainer>
                        {emissionSpools ? (
                            filteredData.length ? (
                                filteredData
                                    .sort((a, b) => sort(a, b, activeSort))
                                    .map((spoolDetails, index) => {
                                        return (
                                            <EmissionSpool
                                                spoolDetails={spoolDetails}
                                                key={index}
                                                collapsed={
                                                    selectedAccordion ===
                                                    spoolDetails.address
                                                }
                                                toggle={toggle}
                                            />
                                        );
                                    })
                            ) : (
                                <EmptyDataRow text='No Spools match your search criteria, please try again.' />
                            )
                        ) : (
                            <EmptyDataRow text='An unexpected error occurred while loading your Spools.' />
                        )}
                    </TableRowContainer>
                    <div className='height-container'>
                        {emissionSpools && <EmissionSpoolsVote />}
                    </div>
                </Loading>
            )}
        </Table>
    );
};

export default EmissionSpools;
